// hooks/useCookieConsent.ts
'use client'

import { useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/navigation'

export type CookieConsentStatus = 'pending' | 'accepted' | 'refused' | 'refused_previously'

export const useCookieConsent = () => {
  const [consentStatus, setConsentStatus] = useState<CookieConsentStatus>('pending')
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()

  useEffect(() => {
    // Vérifier le statut des cookies au chargement
    try {
      const savedConsent = localStorage.getItem('cookie_consent')
      if (savedConsent) {
        const consentData = JSON.parse(savedConsent)
        setConsentStatus(consentData.status)
        
        // Si l'utilisateur a refusé, on le marque comme "refused_previously"
        if (consentData.status === 'refused') {
          setConsentStatus('refused_previously')
        }
      }
    } catch (error) {
      console.error('Erreur lors de la lecture du consentement des cookies:', error)
      // En cas d'erreur, on remet à "pending"
      setConsentStatus('pending')
    }
    setIsLoading(false)
  }, [])

  const acceptCookies = useCallback(() => {
    const consentData = {
      status: 'accepted',
      timestamp: Date.now(),
      version: '1.0'
    }
    try {
      localStorage.setItem('cookie_consent', JSON.stringify(consentData))
      setConsentStatus('accepted')
      
      // Forcer un rafraîchissement pour relancer les appels API
      window.location.reload()
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du consentement:', error)
    }
  }, [])

  const refuseCookies = useCallback(() => {
    const consentData = {
      status: 'refused',
      timestamp: Date.now(),
      version: '1.0'
    }
    try {
      localStorage.setItem('cookie_consent', JSON.stringify(consentData))
      setConsentStatus('refused')
      
      // Rediriger vers le site corporate
      window.location.href =' https://seeric.com/corporate/ '
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du refus:', error)
      // En cas d'erreur, rediriger quand même
      window.location.href = ' https://seeric.com/corporate/ '
    }
  }, [])

  const resetConsent = useCallback(() => {
    try {
      localStorage.removeItem('cookie_consent')
      setConsentStatus('pending')
    } catch (error) {
      console.error('Erreur lors de la suppression du consentement:', error)
    }
  }, [])

  const needsConsent = consentStatus === 'pending' || consentStatus === 'refused_previously'
  const hasAcceptedCookies = consentStatus === 'accepted'

  return {
    consentStatus,
    isLoading,
    acceptCookies,
    refuseCookies,
    resetConsent,
    needsConsent,
    hasAcceptedCookies
  }
}